import React, { useState, useEffect, ChangeEvent, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

// mui components
import { Box, Stack, Typography, LinearProgress, FormControl, Select, MenuItem } from "@mui/material";

// custom components
import CustomLayout from "../../components/layout";
import LoanProgress from "../../components/loan-progress";
import LoanApplicationForm from "../../components/loan-form/create-xml-loan-application";
import EditLoanApplication from "../../components/loan-form/edit-loan-application";
import LoanAddressApplication from "../../components/loan-form/loan-address-application";
import CreditReissueApplication from "../../components/loan-form/credit-reissue-application";
import LoanEmploymentApplication from "../../components/loan-form/loan-employment-application";
import LoanAssetsApplication from "../../components/loan-form/loan-assets-application";
import LoanDemographicApplication from "../../components/loan-form/loan-demographic-application";
import LoanTermsApplication from "../../components/loan-form/create-loan-terms";
import UnderwritingSummary from "../../components/loan-form/underwriting-summary";
import LoanPricingApplication from "../../components/loan-form/loan-pricing-application";
import LoanFeeApplication from "../../components/loan-form/create-loan-fee";
import LoanDocuments from "../../components/loan-form/create-loan-documents";
import SupportingDocs from "../../components/supporting-docs";
import CustomDivider from "../../components/custom-divider";
import DirectPathPrompt from "../../components/direct-path-prompt";
import DirectPathStatus from "../../components/direct-path-status";
import PrimaryButton from "../../components/buttons/primary-button";
import SecondaryButton from "../../components/buttons/secondary-button";
import SecondarySelect from "../../components/custom-select/secondary-select";
import Heading5 from "../../components/typography/heading-05";
import useLoanApplicationNavigation from "../../hooks/useLoanApplicationNavigation";
import Body3 from "../../components/typography/body-03";
import Body2 from "../../components/typography/body-02";
import OrganizationDropdown from "../../components/organization-dropdown";

// helper utils
import { formatBytes } from "../../utils/format-bytes";
import { LOAN_CHANNEL_CONSTANTS } from "../../utils/common-constants";
import {
	isCommercialBrokerChannel,
	numericKeys,
	selectedUserWithLoanChannel,
} from "../../utils/common-helpers";
import { VERBIAGE_CONSTANTS } from "../../utils/verbiage.constants";
import { DIRECT_PATH_CONSTANTS } from "../../utils/direct-path.constants";

// api slices
import { useConvertMismoMutation } from "../../features/convert-mismo/convertMismoApiSlice";
import { useUploadV3XmlMutation } from "../../features/upload-xml/uploadV3XmlApiSlice";
import { useUpdateUploadXmlStatusMutation } from "../../features/upload-xml/updateUploadXmlStatusApiSlice"
import { useDeletePendingApplicationMutation } from "../../features/delete-pending-application/deletePendingApplicationApiSlice";
import { useUpdatePendingApplicationDirectPathMutation } from "../../features/direct-path/updatePendingApplicationDirectPathApiSlice";
import { useUploadXmlHistoryMutation } from "../../features/upload-xml/uploadXmlHistoryApiSlice";
import { useGetMaxLoanMutation } from "../../features/upload-xml/getMaxLoanAmountApiSlice"

// reducer slices
import {
	selectChildOrganizationName,
	selectOrganizationName,
	selectUserDetails,
	selectSelectedOrganization,
} from "../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../features/admin/impersonated-user/impersonatedUserSlice";

// custom modal
import ProcessingModal from "../../components/modal/processing-modal";
import ConfirmationModal from "../../components/modal/confirmation-modal";
import DirectPathSelectionModal from "../../components/modal/direct-path-selection-modal";
import SuccessModal from "../../components/modal/success-modal";
import UploadHistoryTable from "../../components/custom-table/upload-history-table";
import JSZip from 'jszip';
import MaxLoanModal from "../../components/modal/max-loan-amount-check-modal";
import CommanErrorModal from "../../components/modal/common-error-modal";
import { useErrorMessages } from "../../utils/error-messages.constants";

// mui icons
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UploadOutlined from "@mui/icons-material/UploadOutlined";
import ListAltIcon from '@mui/icons-material/ListAlt';
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from "@mui/icons-material/Close";
import { CloseOutlined } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// sort utilities
import { sortDate, sortAmount } from "../../utils/sort-methods";

// custom styles
import styles from "./index.module.scss";

const fileTypes = ["XML"];

export default function NewLoan() {
	const navigate = useNavigate();
	const location = useLocation();
	const { getErrorMessage } = useErrorMessages();
	const [commonErrorData, setCommonErrorData] = useState({});
	const [commonErrorModalVisible, setCommonErrorModalVisible] = useState(false);
	const handleErrorPopup = (errorKey, referenceId) => {
		const errorData = getErrorMessage(errorKey);
		setCommonErrorData({
			...errorData,
			message: errorData.message?.replace("[Reference ID]", referenceId),
		});
		setCommonErrorModalVisible(true);
	};

	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);

	// Simple flag: disable all buttons until organization is selected (for multi-org users)
	const [orgSelected, setOrgSelected] = useState(false);

	useEffect(() => {
		const isMultiOrg = userDetails?.organizations?.length > 1;
		if (!isMultiOrg) {
			// Single-org user — no selection needed
			setOrgSelected(true);
		} else {
			// Multi-org user — only enable after selecting
			setOrgSelected(!!selectedOrganization && selectedOrganization !== "");
		}
	}, [selectedOrganization, userDetails]);

	const shouldDisableButtons = !orgSelected;

	const loanSteps = [
		"Borrower Information",
		"Address",
		"Credit Reissue",
		"Employment and Income",
		"Assets and Liabilities",
		"Declarations and Others",
		"Loan Product and Terms",
		"Underwriting Summary",
		"Loan Pricing",
		`${
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)?.loan_channel === LOAN_CHANNEL_CONSTANTS.WHS
				? "Closing Information"
				: "Fees"
		}`,
		"Supporting Documents",
	];

	const [uploadV3Xml, { isLoading }] = useUploadV3XmlMutation();
	const [convertMismo] = useConvertMismoMutation();
	const [uploadHistoryDataCall] = useUploadXmlHistoryMutation();

	const [addCounter, setAddCounter] = useState([1,2]);
	const [getMaxLoanMutationCall] = useGetMaxLoanMutation();

	const [confirmModalText, setConfirmModalText] = useState("");
	const [showUploadHistory, setShowUploadHistory] = useState(true);
	const [confirmModalVisible, setConfirmModalVisible] = useState(false);
	const [uploadHistoryCount, setUploadHistoryCount] = useState(0);
	const [uploadHistoryLoading, setUploadHistoryLoading] = useState(true);
	const [uploadRefreshTrigger, setUploadRefreshTrigger] = useState(0);

	const [newLoanAmount, setNewLoanAmount] = useState(0)

	const handleOpenConfirmModal = () => {
		setConfirmModalVisible(true);
	};

	const handleUploadHistory = async () => {
		// Show upload history section
		setShowUploadHistory(true);
		setMultiXmlUploaded(false);
		setActiveUploadView("history");
		// Reset single upload states
		setXmlType("");
		setXmlUploaded(false);
		setFile("");
		setAddCounter([1, 2]);
		setMultiLoanData({});
		setShowLoanHistoryNumber({});
		resetDirectPathSelection();
	};

	const handleCloseConfirmModal = () => {
		setConfirmModalVisible(false);
	};

	const handleConfirmExit = () => {
		navigate(`/home`);
	};

	const handleCloseAdminModal = () => {
		navigate(-1);
	};

	const handleSubmitConfirmModal = () => {
		navigate("/admin");
	};

	const delay = (ms) => {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	const organizationName = useSelector(selectOrganizationName);
	const childOrganizationName = useSelector(selectChildOrganizationName);

	useEffect(() => {
		// check if admin signed in as another user has an organization
		console.log("150 user roles:", userDetails?.user_roles);
		console.log("150 organization name:", organizationName);
		
		if (
			userDetails?.user_roles?.includes("admin") &&
			(organizationName === undefined ||
				organizationName === null ||
				organizationName === "")
		) {
			console.log("140 No organization");
			setConfirmModalText(VERBIAGE_CONSTANTS.SIGN_IN_BROKER);
			handleOpenConfirmModal();
		} else {
			console.log("140 organization found:", organizationName);
			handleCloseConfirmModal();
		}
		handleGetMaxLoanAmount()
	}, [organizationName, userDetails]);

	// If navigated with state to open history, honor it
	useEffect(() => {
		console.log("DEBUG openHistory state:", location?.state);
		if (location?.state?.openHistory === true) {
			console.log("DEBUG opening upload history");
			setShowUploadHistory(true);
			setMultiXmlUploaded(false);
			setXmlSubmitted(false);
			setActiveUploadView("history");
			setXmlType(""); // Clear xmlType to hide upload UI
			setXmlUploaded(false);
			setFile("");
			setLoanApplicationStage(0); // Reset to step 0 when navigating from success modal
			setNewLoanAmount(0)
		}
	}, [location]);

	// Prevent repeated upload history API calls; allow refresh when trigger changes
	const lastFetchKeyRef = useRef({ userId: null, refresh: null });
	useEffect(() => {
		const tpoUserId = userDetails?.user_tpo_id || impersonatedUser?.user_tpo_id;
		const refreshKey = uploadRefreshTrigger;
		if (!tpoUserId) return;
		const isSame =
			lastFetchKeyRef.current.userId === tpoUserId &&
			lastFetchKeyRef.current.refresh === refreshKey;
		if (isSame) return;

		lastFetchKeyRef.current = { userId: tpoUserId, refresh: refreshKey };
		const fetchUploadCount = async () => {
			try {
				setUploadHistoryLoading(true);
				const response = await uploadHistoryDataCall({
					tpo_user_id: tpoUserId,
					page: 1,
					pageSize: 25,
					filters: {},
					userDetails: userDetails,
					selectedOrganization: selectedOrganization,
				}).unwrap();

				let count = 0;
				if (Array.isArray(response?.response)) {
					count = response.response.length;
				} else if (Array.isArray(response?.results)) {
					count = response?.count ?? 0;
				} else if (Array.isArray(response)) {
					count = response.length;
				}
				setUploadHistoryCount(count);
			} catch (err) {
				console.error("Error fetching upload count:", err);
			} finally {
				setUploadHistoryLoading(false);
			}
		};
		fetchUploadCount();
	}, [userDetails?.user_tpo_id, impersonatedUser?.user_tpo_id, uploadRefreshTrigger, uploadHistoryDataCall, selectedOrganization]);

	// Refetch upload history when organization ID changes
	const organizationId = useSelector((state) => state.auth.organizationId);
	const lastOrganizationIdRef = useRef(null);
	useEffect(() => {
		// Only trigger refresh when organization ID actually changes
		if (organizationId && organizationId !== lastOrganizationIdRef.current) {
			lastOrganizationIdRef.current = organizationId;
			// Trigger a refresh of upload history data
			setUploadRefreshTrigger(prev => prev + 1);
		}
	}, [organizationId]);

	const [file, setFile] = useState("");

	const [fileUploadModal, setFileUploadModal] = useState(false);
	
	const [xmlUploaded, setXmlUploaded] = useState(false);
	const [xmlMultiUploaded, setMultiXmlUploaded] = useState(false);
	const [xmlSubmitted, setXmlSubmitted] = useState(false);
	const [xmlMultiSubmitted, setXmlMultiSubmitted] = useState(false);

	const [xmlType, setXmlType] = useState("");
	const [activeUploadView, setActiveUploadView] = useState("history");

	const [fileUploadedModal, setFileUploadedModal] = useState(false);
	const [loanData, setLoanData] = useState({});
	const [multiLoanData, setMultiLoanData] = useState({});

	// DirectPath Processing selection is captured per loan and only for the
	// Commercial Broker channel, every other channel keeps its existing workflow.
	const showDirectPath = isCommercialBrokerChannel(
		userDetails,
		impersonatedUser,
		selectedOrganization
	);
	const [directPathSelection, setDirectPathSelection] = useState({});
	const [directPathErrors, setDirectPathErrors] = useState({});
	const [directPathModalVisible, setDirectPathModalVisible] = useState(false);
	// Used when resuming a pending history item (single-loan path, not multi-row).
	const [resumeDirectPath, setResumeDirectPath] = useState(undefined);

	const [loanGuid, setLoanGuid] = useState("");
	const [showLoanHistoryNumber, setShowLoanHistoryNumber] = useState({});
	const [currentItemSet, setCurrentItemSet] = useState(0);
	const [handleDeleteRowModel, setHandleDeleteRowModel] = useState(false);

	const [deletePendingApplication] = useDeletePendingApplicationMutation();
	const [updatePendingApplicationDirectPath] =
		useUpdatePendingApplicationDirectPathMutation();


	const [loanApplicationStage, setLoanApplicationStage] = useState(0);
	const [addItemToDelete, setAddItemToDelete] = useState(0)
	const [maxLoanCheckModal, setMaxLoanCheckModal] = useState(false) 

	const {
		handleNavigateToStage,
		makeRegisterForStage,
		incompleteSections,
		completedSections,
		progressCurrentStep,
		preValidateXmlSections,
		resetSectionProgress,
		validateAllSectionsForSubmit,
		markSectionCompletion,
		setCreditProvidedMode,
	} = useLoanApplicationNavigation({
		loanApplicationStage,
		setLoanApplicationStage,
		loanGuid,
	});

	const getActiveXmlApplicationData = useCallback(() => {
		return xmlMultiSubmitted
			? multiLoanData[currentItemSet] === undefined
				? multiLoanData
				: multiLoanData[currentItemSet]
			: loanData;
	}, [xmlMultiSubmitted, multiLoanData, currentItemSet, loanData]);

	useEffect(() => {
		if (!xmlSubmitted || !loanGuid) {
			return;
		}

		const applicationData = getActiveXmlApplicationData();

		if (!applicationData?.applications?.length) {
			return;
		}

		preValidateXmlSections(applicationData, {
			loanOriginatorEmail: userDetails?.email || "",
			loanProcessorEmail: "",
		});
	}, [
		xmlSubmitted,
		loanGuid,
		loanData,
		multiLoanData,
		currentItemSet,
		xmlMultiSubmitted,
		preValidateXmlSections,
		getActiveXmlApplicationData,
		userDetails?.email,
	]);

	const [maxLoanAmountConstant, setMaxLoanAmountConstant] = useState(0) 

	const handleGetMaxLoanAmount = async () => {
		try {
			const loanAmountData = await getMaxLoanMutationCall().unwrap();
			setMaxLoanAmountConstant(loanAmountData?.response?.data?.max_loan_amount);
		} catch (err) {
			console.log("getMaxLoanMutationCall:", err);
		}
	}

	const resetLoanCreationState = () => {
		setLoanApplicationStage(0);   // Always start at step 0
		setLoanGuid("");              // Clear previous loan GUID
		setXmlSubmitted(false);
		setXmlMultiSubmitted(false);
		setCurrentItemSet(0);
		setLoanData({});
		resetSectionProgress();
	};

	const handleOpenFileUploadModal = () => {
		setFileUploadedModal(true);
	};

	const handleCloseFileUploadModal = () => {
		setFileUploadedModal(false);
		setXmlSubmitted(true);
		if(currentItemSet === 0){
			setXmlMultiSubmitted(false)
		}else{
			setXmlMultiSubmitted(true)
		}
		
		if(multiLoanData[currentItemSet]?.borrowerRequestedLoanAmount > maxLoanAmountConstant){
			setMaxLoanCheckModal(true)
		}
	};

	const handleCloseMaxLoanCheckModal = () => {
		setMaxLoanCheckModal(false)
		setFileUploadedModal(false);
		setXmlSubmitted(true);	
	};

	const handleNewLoanAmountChange = () => {
		if(newLoanAmount > 0){
			setLoanData({...multiLoanData[currentItemSet], borrowerRequestedLoanAmount: newLoanAmount})	
			setMultiLoanData({...multiLoanData[currentItemSet], borrowerRequestedLoanAmount: newLoanAmount})	
		}
	}

	const showUploadFile = (xmlType) => {
		setXmlUploaded(false);
		setFile("");
		setXmlType(xmlType);
		setActiveUploadView("single");
		setLoanApplicationStage(0); // Reset to step 0 for new loan
		// Clear multi-upload data when switching to single upload
		setMultiXmlUploaded(false);
		setShowUploadHistory(false);
		setMultiLoanData({});
		setShowLoanHistoryNumber({});
		setAddCounter([1, 2]);
		resetDirectPathSelection();
	};

	const showMultiUploadFile = async (xmlType) => {
		setMultiXmlUploaded(true);
		setActiveUploadView("multi");
		setLoanApplicationStage(0); // Reset to step 0 for new loan
		// Reset single upload and history states
		setXmlType("");
		setXmlUploaded(false);
		setFile("");
		setShowUploadHistory(false);
		// Only clear if switching from a different mode, preserve if already in multi mode
		if (activeUploadView !== "multi") {
			setMultiLoanData({});
			setShowLoanHistoryNumber({});
			setAddCounter([1, 2]);
			resetDirectPathSelection();
		}
	};

	const resetDirectPathSelection = () => {
		setDirectPathSelection({});
		setDirectPathErrors({});
		setResumeDirectPath(undefined);
	};

    // Clear in-progress XML uploads when the user switches organization.
	// The file stays linked to the previous org in Loan Upload History.
	const prevOrgIdForUploadRef = useRef(organizationId);
	useEffect(() => {
		if (
			prevOrgIdForUploadRef.current &&
			organizationId &&
			organizationId !== prevOrgIdForUploadRef.current
		) {
			setFile("");
			setXmlUploaded(false);
			setAddCounter([1, 2]);
			setMultiLoanData({});
			setShowLoanHistoryNumber({});
			resetDirectPathSelection();
		}
		prevOrgIdForUploadRef.current = organizationId;
	}, [organizationId]);	

	const getLoanHistoryId = (item) =>
		multiLoanData?.[item]?.__historyId
			|| multiLoanData?.[item]?.loan_history_id
			|| multiLoanData?.[item]?.loanHistoryId
			|| multiLoanData?.[item]?.loan_history?.id
			|| multiLoanData?.[item]?.id;

	const handleDirectPathRowSelect = async (item, isDirectPath) => {
		setDirectPathSelection((prevSelection) => ({
			...prevSelection,
			[item]: isDirectPath,
		}));
		setDirectPathErrors((prevErrors) => ({ ...prevErrors, [item]: false }));

		// Keep the selection on the history row so it survives leaving the page
		// before the loan is created.
		const loanHistoryId = getLoanHistoryId(item);
		if (!loanHistoryId) {
			return;
		}

		try {
			await updatePendingApplicationDirectPath({
				loanHistoryId,
				directPath: isDirectPath,
			}).unwrap();
		} catch (err) {
			console.log("updatePendingApplicationDirectPath err:", err);
		}
	};

// The lock-in notice is shown only after the first XML has a DirectPath
	// choice. A second uploaded file hides it for the rest of this session.
	const uploadedXmlCount = Object.values(showLoanHistoryNumber).filter(Boolean)
		.length;
	const hasFirstUploadDirectPathSelection =
		showDirectPath &&
		uploadedXmlCount === 1 &&
		Object.values(directPathSelection).some(
			(selection) => typeof selection === "boolean"
		);

	// Resolve DirectPath for the active XML application without changing non-CB flows.
	const getActiveDirectPath = () => {
		if (!showDirectPath) {
			return undefined;
		}
		if (xmlMultiSubmitted) {
			return directPathSelection[currentItemSet];
		}
		if (typeof resumeDirectPath === "boolean") {
			return resumeDirectPath;
		}
		return directPathSelection[currentItemSet];
	};

	const handleOpenLoanFromRow = (item) => {
		if (showDirectPath && directPathSelection[item] === undefined) {
			setDirectPathErrors((prevErrors) => ({ ...prevErrors, [item]: true }));
			return;
		}

		setResumeDirectPath(undefined);
		handleOpenFileUploadModal();
		setCurrentItemSet(item);
	};

	const handleCloseDirectPathModal = () => {
		setDirectPathModalVisible(false);
	};

	const handleConfirmDirectPathModal = (isDirectPath) => {
		setDirectPathModalVisible(false);
		navigate("/create-loan", { state: { directPath: isDirectPath } });
	};

	const handleManualLoan = () => {
		setLoanData({});

		if (showDirectPath) {
			setDirectPathModalVisible(true);
			return;
		}

		navigate("/create-loan");
	};

	const handleNewXml = async (file) => {
		setFileUploadModal(true);
		setFile(file);
		setXmlMultiSubmitted(false)
		setLoanApplicationStage(0); // Reset to step 0 for new loan
		console.log("87 file:", file);

		var formData = new FormData();
		var xmlFile = file;
		formData.append("file", xmlFile);
		formData.append("tpo_user_id", userDetails?.user_tpo_id);

		// Display the key/value pairs
		for (var pair of formData.entries()) {
			console.log(pair[0] + ", " + pair[1]);
		}

		try {
			const fileUpload = await uploadV3Xml({ 
				formData,
				userDetails: userDetails,
				selectedOrganization: selectedOrganization,
			}).unwrap();
			console.log("200 v3 xml file data:", fileUpload);
			// uploaded successfully
			setXmlUploaded(true);
			// Use fileUpload.data if available, otherwise use the entire response
			setLoanData(fileUpload?.data || fileUpload);
			setFileUploadModal(false);
			// Refresh upload history count
			setUploadRefreshTrigger(prev => prev + 1);
		} catch (err) {
			console.log("95 err:", err);
			handleErrorPopup("XML_UPLOAD_FAILED", err?.data?.reference_id);
			setFileUploadModal(false);
		}
	};

	const handleUploadHistoryRowClick = (item) => {
		const status = (item?.status || "").toLowerCase().trim();
		const isPending = status.includes("pending");
		const isCreated = status.includes("created");
		const isSubmitted = status.includes("submitted");
		const isCompleted = status.includes("completed") || isCreated || isSubmitted;
		const isPendingApplicationSubmission = status.includes("pending application submission");

		// Pending Application Submission: navigate to edit-loan
		if (isPendingApplicationSubmission) {
			const guid =
				item?.loan_guid ||
				item?.loanGuid ||
				item?.loan_id ||
				item?.loanId ||
				item?.guid;
			if (guid) {
				navigate(`/edit-loan/${guid}`);
				return;
			}
		}

		if (isCompleted) {
			const guid =
				item?.loan_guid ||
				item?.loanGuid ||
				item?.loan_id ||
				item?.loanId ||
				item?.guid;
			if (guid) {
				navigate(`/loan-summary/${guid}`);
				return;
			}
			// If completed/created but GUID missing, route to pipeline as best-effort
			navigate("/pipeline");
			return;
		}

		// Pending: open modal to review/submit
		resetLoanCreationState();
		if (showDirectPath) {
			const historyDirectPath = item?.direct_path ?? item?.directPath;
			setResumeDirectPath(
				typeof historyDirectPath === "boolean" ? historyDirectPath : undefined
			);
		} else {
			setResumeDirectPath(undefined);
		}
		handleOpenFileUploadModal();
		if (item?.load_data) {
			try {
				setLoanData(JSON.parse(item?.load_data));
			} catch (_) {}
		}
	};

	// If navigated from main Upload History with a specific pending item, auto-open it
	useEffect(() => {
		if (location?.state?.uploadHistoryItem && !xmlSubmitted) {
			handleUploadHistoryRowClick(location.state.uploadHistoryItem);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location?.state?.uploadHistoryItem]);

	// A recycled row id would inherit the deleted row's parsed loan data, so the
	// next line always continues past every id the page has already used.
	const getNextRowId = () => {
		const usedIds = [
			...addCounter,
			...numericKeys(multiLoanData),
			...numericKeys(showLoanHistoryNumber),
		];
		return (usedIds.length ? Math.max(...usedIds) : 0) + 1;
	};

	const handleAddRowbutton = () => {
		const nextRowId = getNextRowId();
		setAddCounter((prevRows) => [...prevRows, nextRowId]);
	}

	const handleRemoveRowbutton = (inputitem) => {
		setMultiLoanData(({ [inputitem]: removedLoan, ...restLoans }) => restLoans);
		setShowLoanHistoryNumber(
			({ [inputitem]: removedRow, ...restRows }) => restRows
		);
		setAddCounter( prevRows => prevRows.filter(item => item !== inputitem))
		setAddItemToDelete(0)
		setHandleDeleteRowModel(false)
		setDirectPathSelection(
			({ [inputitem]: removedSelection, ...restSelection }) => restSelection
		);
		setDirectPathErrors(
			({ [inputitem]: removedError, ...restErrors }) => restErrors
		);
		if(addCounter.length === 1){
			setShowLoanHistoryNumber({})
			setMultiLoanData({})
			setAddCounter([1,2])
			setMultiXmlUploaded(false);
			setShowUploadHistory(false)
			resetDirectPathSelection();
		}
	}

	const handleConfirmDeleteRow = async () => {
		const item = addItemToDelete;
		const historyId = getLoanHistoryId(item);

		setHandleDeleteRowModel(false);
		if (historyId) {
			try {
				await deletePendingApplication({ loan_history_id: historyId }).unwrap();
				// Refresh upload history count after successful deletion
				setUploadRefreshTrigger(prev => prev + 1);
			} catch (err) {
				console.log("deletePendingApplication error:", err);
			}
		}
		// Remove from UI regardless to avoid stuck state
		handleRemoveRowbutton(item);
	}

	const handleOpenRowDeleteModal = (passedItem) => {
		setAddItemToDelete(passedItem)
		setHandleDeleteRowModel(true)
	}

	const checkMaxDataKey = (data) => {
		return Math.max(...Object.keys(data))
	}

	const generateArray = (gLength, startFrom = 1) => {
		const tempArr = []
		for(let i=0; i<gLength; i++){
			tempArr.push(startFrom + i)
		}
		return tempArr
	}

	const handleMultipleupload = async(filelist, item) => {
		if (!filelist || filelist.length === 0) return;
		const isBatch = filelist.length > 1;
		if (isBatch) {
			setFileUploadModal(true);
		}

		try {
			if (isBatch) {
				// Find empty rows and fill them first
				const emptySlots = addCounter.filter(id => !showLoanHistoryNumber[id]);
				const filesToUse = [];
				let nextNewId = addCounter.length ? Math.max(...addCounter) + 1 : 3;
				
				for(let i = 0; i < filelist.length; i++){
					let assignedId;
					if (i < emptySlots.length) {
						assignedId = emptySlots[i]; // Use empty slot
					} else {
						assignedId = nextNewId;
						nextNewId++;
					}
					filesToUse.push({ file: filelist[i], id: assignedId });
				}
				
				// Update addCounter to include new IDs if any
				setAddCounter(prev => {
					const updated = new Set(prev);
					filesToUse.forEach(f => updated.add(f.id));
					return Array.from(updated).sort((a, b) => a - b);
				});
				
				// Upload each file
				for(let i = 0; i < filesToUse.length; i++){
					await handleMultiXml(filesToUse[i].file, filesToUse[i].id, true);
					await delay(500);
				}
			} else {
				await handleMultiXml(filelist[0], item);
			}
		} finally {
			if (isBatch) {
				setFileUploadModal(false);
			}
		}
		await delay(500)
	}



	const handleMultiXml = async (file, item, suppressModal = false) => {
		if (!suppressModal) {
			setFileUploadModal(true);
		}
		setFile(file);
		setLoanApplicationStage(0); // Reset to step 0 for new loan
		if(file.type === 'application/x-zip-compressed'){
			await handleZipFileUploadChange(file, suppressModal, item);
			if (!suppressModal) {
				setFileUploadModal(false);
			}
			return;
		}else{
			var formData = new FormData();
			var xmlFile = file;
			formData.append("file", xmlFile);
			formData.append("tpo_user_id", userDetails?.user_tpo_id);

			try {
				const fileUpload = await uploadV3Xml({ 
				formData,
				userDetails: userDetails,
				selectedOrganization: selectedOrganization,
			}).unwrap();
				const respData = fileUpload?.data || fileUpload;
				const historyId = respData?.loan_history_id
					|| respData?.loanHistoryId
					|| respData?.loan_history?.id
					|| fileUpload?.loan_history_id
					|| fileUpload?.history_id
					|| respData?.id;
				setShowLoanHistoryNumber(Object.assign(showLoanHistoryNumber, {[item]: true}));
				setMultiLoanData(Object.assign(multiLoanData, {[item]: { ...respData, __historyId: historyId }}));
				// Refresh upload history count
				setUploadRefreshTrigger(prev => prev + 1);
				await delay(1000)
			} catch (err) {
				console.log("95 err:", err);
				handleErrorPopup("XML_UPLOAD_FAILED", err?.data?.reference_id);
			} finally {
				if (!suppressModal) {
					setFileUploadModal(false);
				}
			}
		}
	};

	const handleUploadCalyx = async (file) => {
		setFileUploadModal(true);
		setFile(file);
		setLoanApplicationStage(0); // Reset to step 0 for new loan
		console.log("125 file:", file);

		var formData = new FormData();
		var xmlFile = file;
		formData.append("Calyx_XML", xmlFile);

		// Display the key/value pairs
		for (var pair of formData.entries()) {
			console.log(pair[0] + ", " + pair[1]);
		}

		try {
			const fileUpload = await convertMismo({ formData }).unwrap();
			console.log("140 uploaded file data:", fileUpload);
			// uploaded successfully
			setXmlUploaded(true);
			// Use fileUpload.data if available, otherwise use the entire response
			setLoanData(fileUpload?.data || fileUpload);
			setFileUploadModal(false);
			// Refresh upload history count
			setUploadRefreshTrigger(prev => prev + 1);
		} catch (err) {
			console.log("150 err:", err);
			setFileUploadModal(false);
		}
	};

	const xmlToBinary = (xmlString, filename) => {
		const binaryFile = new File([xmlString], filename, {
			type: "text/xml",
		  });
		return binaryFile;
	  }

	const checkCurrentItemSet = () => {
		return multiLoanData[currentItemSet] == undefined ? multiLoanData : multiLoanData[currentItemSet];
	}

	const handleZipFileUploadChange = async (file, suppressModal = false, targetId) => {
		if (file) {
		  try {
			if (!suppressModal) {
				setFileUploadModal(true);
			}
			const zip = new JSZip();
			const loadedZip = await zip.loadAsync(file);
	
			const files = [];
			for (const filename in loadedZip.files) {
			  if (!loadedZip.files[filename].dir) {
				const content = await loadedZip.files[filename].async('string');
				files.push({ name: filename, content: content });
			  }
			}
			let assignedIds = [];
			let nextIdSeed = addCounter.length ? Math.max(...addCounter) + 1 : 1;
			const targetAvailable = targetId && !showLoanHistoryNumber[targetId];

			for(let i = 0; i < files.length; i++){
				let id;
				if (i === 0 && targetAvailable) {
					id = targetId;
				} else {
					while (addCounter.includes(nextIdSeed) || showLoanHistoryNumber[nextIdSeed]) {
						nextIdSeed++;
					}
					id = nextIdSeed;
					nextIdSeed++;
				}
				assignedIds.push(id);
			}

			setAddCounter(prev => {
				const merged = new Set(prev);
				assignedIds.forEach(id => merged.add(id));
				// drop leading placeholder rows (1,2) if they remain empty
				[1,2].forEach(ph => {
					if (!showLoanHistoryNumber[ph] && !assignedIds.includes(ph)) {
						merged.delete(ph);
					}
				});
				return Array.from(merged).sort((a, b) => a - b);
			});

			for(let i = 0;i<files.length;i++){
				await handleMultiXml(xmlToBinary(files[i].content, files[i].name), assignedIds[i], true)
				await delay(500)
			}
			await delay(500)
		  } catch (error) {
			console.error('Error reading or extracting zip file:', error);
			alert('Error reading zip file. Please try again.');
		  } finally {
			if (!suppressModal) {
				setFileUploadModal(false);
			}
		  }
		}
	}

	const deleteFile = (event) => {
		event.stopPropagation();
		event.preventDefault();

		setXmlUploaded(false);
		setFile("");

		console.log("deelte file");
	};

	const breadcrumbData = [
		{
			label: "Home",
			path: "/home",
		},
		{
			label: "New Loan",
			path: "",
		},
	];

	return (
		<>
		<Box>
			<CustomLayout breadcrumb={breadcrumbData} disabledOverflow={true}>
				<Stack
					direction="row"
					className={styles.stackContainer}
					sx={{
						overflow: "hidden",
					}}
				>
					{/* loan status */}
					<Stack
						direction={xmlSubmitted ? "row" : "column"}
						className={
							xmlSubmitted
								? styles.bodyContainer
								: styles.bodyContainer + " " + styles.bodySpacing
						}
					>
						{!xmlSubmitted && (
							<Stack
							direction="column"
							className={styles.newLoanButtonsContainer}
						>
							<Stack direction="column" className={styles.verticalButtonsContainer}>
								<Stack direction="row" className={styles.buttonWithDescription}>
									<Stack direction="column" className={styles.buttonTextContainer}>
										<Body2 text="Organization Selection *" extraClass={styles.buttonTitle} style={{ color: 'red' }} />
										<Body3 text="Through which organization are you delivering?" extraClass={styles.buttonDescription} />
									</Stack>
									<Box sx={{ width: '250px' }}>
										<OrganizationDropdown extraClass={styles.newLoanOrgDropdown} />
									</Box>
								</Stack>
								
								<Stack direction="row" className={styles.buttonWithDescription}>
									<SecondaryButton
										startIcon={<EditOutlinedIcon sx={{ fontSize: "20px" }} />}
										text="Fill Application Manually"
										extraClass={styles.applicationTypeBtn}
										onClick={handleManualLoan}
										disabled={shouldDisableButtons}
									/>
									<Body3 text="Begin a new application and enter all loan details manually." extraClass={styles.buttonDescription} />
								</Stack>

								<Stack direction="row" className={styles.buttonWithDescription}>
									<SecondaryButton
										startIcon={<UploadOutlined sx={{ fontSize: "20px" }} />}
										text="Upload Single or Multiple 3.4 XML"
										extraClass={
											activeUploadView === "multi"
												? styles.selectedApplicationTypeBtn
												: styles.applicationTypeBtn
										}
										onClick={() => showMultiUploadFile()}
										disabled={shouldDisableButtons}
									/>
									<Body3 text="Upload one or more loan files in MISMO 3.4 XML format (zip files supported)." extraClass={styles.buttonDescription} />
								</Stack>

								<Stack direction="row" className={styles.buttonWithDescription}>
									<SecondaryButton
										startIcon={<ListAltIcon sx={{ fontSize: "20px" }} />}
										text={`Loan Upload History (${uploadHistoryLoading ? "..." : uploadHistoryCount})`}
										extraClass={
											activeUploadView === "history"
												? styles.selectedApplicationTypeBtn
												: styles.applicationTypeBtn
										}
										onClick={handleUploadHistory}
										disabled={shouldDisableButtons}
									/>
									<Body3 text="View previously uploaded files." extraClass={styles.buttonDescription} />
								</Stack>
							</Stack>
						</Stack>
					)}

						{!xmlSubmitted && xmlType !== "" && (
							<CustomDivider extraClass={styles.dividerContainer} />
						)}

						{!xmlSubmitted && !xmlMultiUploaded && xmlType !== "" && (
							<SupportingDocs
								fileTypes={fileTypes}
								text={`or drag to upload ${
									xmlType === "ULAD/ILAD MISMO" ? "3.4 XML" : "Calyx XML"
								} file`}
								handleFileAttach={
									xmlType === "ULAD/ILAD MISMO"
										? handleNewXml
										: handleUploadCalyx
								}
								multiple={false}
							/>
						)}

						{!showUploadHistory && xmlMultiUploaded && !xmlSubmitted && (
							<>
							<CustomDivider extraClass={styles.dividerContainer} />
							   {/* Table Headings Row: Only show if any file is uploaded */}
							   {Object.values(multiLoanData).length > 0 && Object.values(multiLoanData).some(data => data && (data.applications?.[0]?.borrower?.lastName || data.applications?.[0]?.borrower?.mailingAddress?.urla2020StreetAddress)) ? (
							   <Box sx={{ width: '100%' }}>
								   <Box display="flex" alignItems="center" width="100%" className={styles.loanStack}>
									   <Box sx={{ flex: '0 0 220px' }} />
									   <Box sx={{ flex: '0 0 380px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
										   <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
											   Go To Loan <ArrowForwardIcon sx={{ fontSize: '18px', verticalAlign: 'middle' }} />
										   </Typography>
									   </Box>
									   <Box sx={{ flex: '0 0 80px' }} />
								   </Box>
							   </Box>
							  ) : null}
							   {addCounter?.map((item) => (
								   <Box key={item} sx={{ width: '100%' }}>
									   <Box display="flex" alignItems="center" width="100%" className={styles.loanStack}>
									   	   <Box display="flex" justifyContent="flex-start" sx={{ flex: '0 0 220px' }}>
											   <SupportingDocs
												   fileTypes={['XML', 'zip']}
												   text={`or drag to upload 3.4 XML file`}
												   handleFileAttach={(file) => {
													   handleMultipleupload(file, item)
												   }}
												   multiple
												   flat
											   />
										   </Box>
										   {/* Column 3: Review/Primary button */}
										<Box display="flex" alignItems="center" sx={{ flex: '0 0 280px' }}>
											   {showLoanHistoryNumber[item] && !xmlSubmitted && (
										   	   	   	   <Box sx={{ width: 280 }}>
													   <PrimaryButton
														   text={`${multiLoanData[item]?.applications?.[0]?.borrower?.lastName || 'Borrower'} - ${multiLoanData[item]?.applications?.[0]?.borrower?.mailingAddress?.urla2020StreetAddress || 'Unknown Address'}`}
														   onClick={() => handleOpenLoanFromRow(item)}
														   fullWidth
														   sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
													   />
												   </Box>
											   )}
										   </Box>
										   {/* DirectPath status + remove control stay in one aligned group */}
										   <Box
											   display="flex"
											   alignItems="center"
											   className={styles.directPathActionGroup}
										   >
											   {showDirectPath &&
												   showLoanHistoryNumber[item] &&
												   !xmlSubmitted &&
												   directPathSelection[item] !== undefined && (
													   <Box className={styles.directPathStatusContainer}>
														   <DirectPathStatus
															   selected={directPathSelection[item]}
															   showLabel
															   onChange={(isDirectPath) =>
																   handleDirectPathRowSelect(item, isDirectPath)
															   }
														   />
													   </Box>
												   )}
											   {showLoanHistoryNumber[item] && !xmlSubmitted && (
												   <Box
													   sx={{ cursor: 'pointer' }}
													   onClick={() => handleOpenRowDeleteModal(item)}
													   className={styles.deleteIconContainer}
												   >
													   <CloseIcon className={styles.deleteRowIcon} />
												   </Box>
											   )}
										   </Box>
									   </Box>

									   {/* DirectPath selection is captured per loan before a loan number exists */}
									   {showDirectPath &&
										   showLoanHistoryNumber[item] &&
										   !xmlSubmitted &&
										   directPathSelection[item] === undefined && (
											   <Box className={styles.directPathPromptContainer}>
												   <DirectPathPrompt
													   error={directPathErrors[item]}
													   onSelect={(isDirectPath) =>
														   handleDirectPathRowSelect(item, isDirectPath)
													   }
												   />
											   </Box>
										   )}
								   </Box>
							   ))}
							   <Box sx={{ mt: 2 }}>
								   <SecondaryButton
									   startIcon={<AddCircleIcon sx={{ color: '#49787f', mr: 0.75 }} />}
									   text="Add New Line"
									   onClick={handleAddRowbutton}
									   withIcon
								   />
							   </Box>

							    {hasFirstUploadDirectPathSelection && (
								   <Body3
									   text={DIRECT_PATH_CONSTANTS.POST_SELECTION_NOTICE}
									   extraClass={styles.directPathNoticeText}
								   />
							   )}
							</>
							
							
						)}

						{showUploadHistory && !xmlMultiUploaded && !xmlSubmitted && !shouldDisableButtons && !location?.state?.fromUploadHistory && (
							<UploadHistoryTable
								key={`${selectedOrganization}-${uploadRefreshTrigger}`}
								pageSize={25}
								maxHeight={"calc(60vh)"}
								minHeight={"calc(30vh)"}
								isFullPage={false}
								fixedHeader={true}
								onRowClick={handleUploadHistoryRowClick}
								onCountChange={setUploadHistoryCount}
								fetchCount={true}
							/>
						)}

						{/* xml uploaded but not submitted */}
						{xmlUploaded && !xmlSubmitted && (
							<Stack
								direction="column"
								className={styles.uploadedFileContainer}
							>
								<Stack direction="row" className={styles.fileListContainer}>
									<Box className={styles.fileIconContainer}>
										<UploadFileIcon className={styles.fileIcon} />
									</Box>
									<Stack
										direction="column"
										className={styles.fileNameContainer}
									>
										<Body2 text={file?.name} />
										<Body3 text={formatBytes(file?.size)} />
									</Stack>

									<Box
										sx={{ cursor: "pointer" }}
										onClick={(event) => deleteFile(event)}
										className={styles.deleteIconContainer}
									>
										<CloseIcon className={styles.deleteIcon} />
									</Box>
								</Stack>

								<Stack direction="row" className={styles.footerBtnContainer}>
									<SecondaryButton
										text="Cancel"
										onClick={(event) => deleteFile(event)}
									/>
									<PrimaryButton
										text={
											xmlType === "CALYX MISMO" ? "Submit" : "Submit Request"
										}
										onClick={handleOpenFileUploadModal}
									/>
								</Stack>
							</Stack>
						)}

						{/* xml submitted */}
						{xmlSubmitted && (
							<Stack direction="column" className={styles.loanContainer}>
								{/* page title and close btn container */}
								<Box direction="row" className={styles.headerContainer}>
									<Stack className={styles.headerTextContainer}>
										<Heading5
											text={`Step ${
												loanApplicationStage === 0
													? loanApplicationStage + 1
													: loanApplicationStage
											}: ${
												loanSteps[
													loanApplicationStage === 0
														? loanApplicationStage
														: loanApplicationStage - 1
												]
											}`}
											fontType="semibold"
											className={styles.headerText}
										/>
									</Stack>

									<Stack
										className={styles.closeIconContainer}
										onClick={() => {
											setConfirmModalText(VERBIAGE_CONSTANTS.SURE_EXIT);
											handleOpenConfirmModal();
										}}
									>
										<CloseOutlined className={styles.closeIcon} />
									</Stack>
								</Box>
								{console.log('multiLoanData[currentItemSet]',xmlMultiSubmitted,loanData, multiLoanData, currentItemSet)}
								{loanApplicationStage === 0 ? (
									<LoanApplicationForm
										setLoanApplicationStage={setLoanApplicationStage}
										setLoanGuid={setLoanGuid}
										loanGuid={loanGuid}
										xmlType={xmlMultiSubmitted ? "ULAD/ILAD MISMO" : xmlType}
										applicationData={xmlMultiSubmitted ? checkCurrentItemSet() : loanData}
										setApplicationData={xmlMultiSubmitted ? setMultiLoanData : setLoanData}
										organizationName={selectedOrganization || organizationName}
										childOrganizationName={childOrganizationName}
										registerSectionHandler={makeRegisterForStage(0)}
										currentStage={1}
										directPath={getActiveDirectPath()}
									/>
								) : loanApplicationStage === 1 ? (
									<EditLoanApplication
										setLoanApplicationStage={setLoanApplicationStage}
										applicationData={xmlMultiSubmitted ? checkCurrentItemSet() : loanData}
										setApplicationData={xmlMultiSubmitted ? setMultiLoanData : setLoanData}
										loanGuid={loanGuid}
										setLoanGuid={setLoanGuid}
										organizationName={selectedOrganization || organizationName}
										childOrganizationName={childOrganizationName}
										registerSectionHandler={makeRegisterForStage(1)}
										currentStage={1}
									/>
								) : loanApplicationStage === 2 ? (
									<LoanAddressApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(2)}
										currentStage={2}
										markSectionCompletion={markSectionCompletion}
										setCreditProvidedMode={setCreditProvidedMode}
									/>
								) : loanApplicationStage === 3 ? (
									<CreditReissueApplication
										setLoanApplicationStage={setLoanApplicationStage}
										setApplicationData={setLoanData}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(3)}
										currentStage={3}
									/>
								) : loanApplicationStage === 4 ? (
									<LoanEmploymentApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(4)}
										currentStage={4}
									/>
								) : loanApplicationStage === 5 ? (
									<LoanAssetsApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(5)}
										currentStage={5}
									/>
								) : loanApplicationStage === 6 ? (
									<LoanDemographicApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(6)}
										currentStage={6}
									/>
								) : loanApplicationStage === 7 ? (
									<LoanTermsApplication
										data={xmlMultiSubmitted ? multiLoanData[currentItemSet] : loanData}
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(7)}
										currentStage={7}
									/>
								) : loanApplicationStage === 8 ? (
									<UnderwritingSummary
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(8)}
										currentStage={8}
									/>
								) : loanApplicationStage === 9 ? (
									<LoanPricingApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(9)}
										incompleteSections={incompleteSections}
										loanSteps={loanSteps}
										currentStage={9}
									/>
								) : loanApplicationStage === 10 ? (
									<LoanFeeApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(10)}
										currentStage={10}
									/>
								) : (
									loanApplicationStage === 11 && (
										<LoanDocuments
											setLoanApplicationStage={setLoanApplicationStage}
											loanGuid={loanGuid}
											registerSectionHandler={makeRegisterForStage(11)}
											validateAllSectionsForSubmit={validateAllSectionsForSubmit}
											loanSteps={loanSteps}									
											currentStage={11}
											markSectionCompletion={markSectionCompletion}
											directPath={getActiveDirectPath()}
										/>
									)
								)}
							</Stack>
						)}

						{/* application progress */}
						{xmlSubmitted && (
							<LoanProgress
								loanSteps={loanSteps}
								currentStep={progressCurrentStep}
								clickNavigation={Boolean(loanGuid)}
								onNavigateToStage={handleNavigateToStage}
								incompleteSections={incompleteSections}
								completedSections={completedSections}
								loanCreated={Boolean(loanGuid)}
							/>
						)}
					</Stack>
				</Stack>
			</CustomLayout>

			{/* modal */}
			<ProcessingModal
				open={fileUploadModal}
				text="Your file is being uploaded and processed"
			/>
			<CommanErrorModal
				isOpen={commonErrorModalVisible}
				onClose={() => setCommonErrorModalVisible(false)}
				errorCode={commonErrorData.code}
				{...commonErrorData}
			/>
			<SuccessModal
				open={fileUploadedModal}
				handleClose={handleCloseFileUploadModal}
				text={
					location?.state?.fromUploadHistory 
						? "Your pending application is ready for review. Complete and submit the application."
						: `Your ${
							xmlType === "CALYX MISMO" ? "CALYX POINT XML" : "3.4"
						} file has been accepted. Review and submit the
						application.`
				}
				confirmText={"Review"}
			/>
			<ConfirmationModal
				open={confirmModalVisible}
				text={confirmModalText}
				confirmText={
					confirmModalText?.includes("exit") ? "Exit Application" : "Sign In"
				}
				handleConfirm={
					confirmModalText?.includes("exit")
						? handleConfirmExit
						: handleSubmitConfirmModal
				}
				closeText={
					confirmModalText?.includes("exit") ? "Return to Application" : "Close"
				}
				handleClose={
					confirmModalText?.includes("exit")
						? handleCloseConfirmModal
						: handleCloseAdminModal
				}
			/>
			<ConfirmationModal
				open={handleDeleteRowModel}
				text={`Are you sure want to delete this application '${multiLoanData[addItemToDelete]?.applications[0]?.borrower?.lastName + ' - ' + multiLoanData[addItemToDelete]?.applications[0]?.borrower?.mailingAddress?.urla2020StreetAddress}'?`}
				confirmText="Delete application now"
				handleConfirm={handleConfirmDeleteRow}
				closeText="Close"
				handleClose={
					() => setHandleDeleteRowModel(false)
				}
			/>
			<DirectPathSelectionModal
				open={directPathModalVisible}
				organizationName={selectedOrganization || organizationName}
				handleClose={handleCloseDirectPathModal}
				handleConfirm={handleConfirmDirectPathModal}
			/>
			<MaxLoanModal
				open={maxLoanCheckModal}
				maxLoanAmountConstant={maxLoanAmountConstant}
				handleClose={handleCloseMaxLoanCheckModal}
				text="Your requested loan amount exceeds the max allowable loan amount. Are you sure this the correct loan amount?"
				confirmText="Save and Continue"
				setNewLoanAmount={setNewLoanAmount}
				requestedLoanAmount={multiLoanData[currentItemSet]?.borrowerRequestedLoanAmount}
				handleNewLoanAmountChange={handleNewLoanAmountChange}
			/>
		</Box>
		</>
	);
}
